/**
 * SSE 管理器 - 用于接收 AI 流式响应
 * 
 * SSE (Server-Sent Events) 是一种基于 HTTP 的服务器推送技术
 * 相比 WebSocket 更简单，适合单向推送场景
 */

import { tokenStorage } from '~/utils/storage'
import { showTopNotification } from '@/composables/useTopNotification'
import { useUserStore } from '~/stores/user'

interface SSEConfig {
    baseUrl: string
    reconnectInterval?: number // 重连间隔
    heartbeatInterval?: number // 心跳间隔
    heartbeatTimeout?: number // 心跳超时时间
    maxReconnectAttempts?: number // 最大重连次数
}

interface SSECallbacks {
    onOpen?: () => void
    onMessage?: (data: any) => void
    onError?: (error: Event) => void
    onClose?: (event: CloseEvent) => void
    onRetry?: (attempt: number) => void
}

interface MessageChunk {
    data?: string
    event?: string
    id?: string
    retry?: number
}

class SSEManager {
    private eventSource: EventSource | null = null
    private config: Required<SSEConfig>
    private callbacks: SSECallbacks = {}

    // 连接状态
    private isConnected = false
    private isConnecting = false
    private connectionError: string | null = null

    // 重连相关
    private reconnectAttempts = 0
    private reconnectTimer: NodeJS.Timeout | null = null
    private heartbeatTimer: NodeJS.Timeout | null = null
    private lastHeartbeatTime = 0
    private manualClose = false

    // 消息缓冲（处理分片）
    private messageBuffer = ''
    private currentEventType = ''
    private currentMsgId: string | null = null

    // 统计
    private messagesReceived = 0
    private messagesSent = 0

    // 错误日志
    private errorLogs: Array<{ type: string; timestamp: string; info: any }> = []

    constructor(config: SSEConfig) {
        this.config = {
            baseUrl: config.baseUrl,
            reconnectInterval: config.reconnectInterval ?? 1000,
            heartbeatInterval: config.heartbeatInterval ?? 5000,
            heartbeatTimeout: config.heartbeatTimeout ?? 30000,
            maxReconnectAttempts: config.maxReconnectAttempts ?? 10
        }
    }

    /**
     * 获取 SSE 连接 URL（带 token）
     */
    private getConnectionUrl(): string {
        const token = tokenStorage.getToken() || 'e5f8297389e2ae33f6417d2b908da160'

        // 清理 baseUrl 中可能已存在的所有 token 相关参数
        let cleanBaseUrl = this.config.baseUrl

        // 移除 ?token& 或 ?token=xxx& 或 &token=xxx 或 ?token 等各种形式
        cleanBaseUrl = cleanBaseUrl
            .replace(/([?&])token=[^&]*/g, '')    // 移除 token=xxx
            .replace(/([?&])token(&|$)/g, '$2')    // 移除单独的 token（无值）
            .replace(/\?&/g, '?')                   // 修复 ?& -> ?
            .replace(/&\?/g, '?')                   // 修复 &? -> ?
            .replace(/\?$/, '')                     // 移除尾部 ?
            .replace(/&$/, '')                      // 移除尾部 &

        // 移除尾部可能的 /
        cleanBaseUrl = cleanBaseUrl.replace(/\/$/, '')

        // 添加新的 token 参数
        return `${cleanBaseUrl}?token=${token}`
    }

    /**
     * 注册回调函数
     */
    on(callbacks: SSECallbacks): void {
        this.callbacks = { ...this.callbacks, ...callbacks }
    }

    /**
     * 移除回调函数
     */
    off(event: keyof SSECallbacks): void {
        if (this.callbacks[event]) {
            this.callbacks[event] = undefined
        }
    }

    /**
     * 连接 SSE
     */
    connect(): void {
        // 防止重复连接
        if (this.isConnecting || (this.isConnected && this.eventSource)) {
            console.log('🔌 SSE 已在连接中或已连接，跳过', {
                isConnecting: this.isConnecting,
                isConnected: this.isConnected
            })
            return
        }

        // 清理旧连接
        this.cleanup()

        console.log('🔌 开始连接 SSE...', {
            attempt: this.reconnectAttempts + 1,
            url: this.getConnectionUrl().replace(/token=\w+/, 'token=***')
        })

        this.isConnecting = true
        this.connectionError = null
        this.manualClose = false

        try {
            // 创建 SSE 连接
            this.eventSource = new EventSource(this.getConnectionUrl())

            // 连接超时保护（15秒）
            const connectionTimeout = setTimeout(() => {
                if (this.isConnecting && !this.isConnected) {
                    console.error('❌ SSE 连接超时（15秒），关闭连接')
                    this.handleClose(new CloseEvent('timeout', { wasClean: false }))
                }
            }, 15000)

            // 连接打开
            this.eventSource.onopen = (event) => {
                clearTimeout(connectionTimeout)
                console.log('✅ SSE 连接成功')
                this.isConnected = true
                this.isConnecting = false
                this.reconnectAttempts = 0
                this.connectionError = null

                // 启动心跳检测
                // this.startHeartbeat()

                // 触发回调
                this.callbacks.onOpen?.()
            }

            // 接收消息
            this.eventSource.onmessage = (event) => {
                this.handleMessage(event)
            }

            // 错误处理
            this.eventSource.onerror = (event) => {
                clearTimeout(connectionTimeout)
                console.error('❌ SSE 错误', {
                    readyState: this.eventSource?.readyState,
                    isConnecting: this.isConnecting,
                    isConnected: this.isConnected
                })

                this.connectionError = 'SSE 连接错误'
                this.isConnecting = false
                this.isConnected = false

                // 记录错误日志
                this.errorLogs.push({
                    type: 'error',
                    timestamp: new Date().toISOString(),
                    info: { readyState: this.eventSource?.readyState }
                })
                // 只保留最近50条日志
                if (this.errorLogs.length > 50) {
                    this.errorLogs = this.errorLogs.slice(-50)
                }

                // 触发回调
                this.callbacks.onError?.(event)

                // 关闭连接并尝试重连
                this.handleClose(event)
            }

        } catch (error) {
            console.error('❌ 创建 SSE 失败:', error)
            this.connectionError = `创建连接失败: ${error instanceof Error ? error.message : String(error)}`
            this.isConnecting = false
            this.isConnected = false

            // 记录错误日志
            this.errorLogs.push({
                type: 'error',
                timestamp: new Date().toISOString(),
                info: { message: String(error) }
            })
            if (this.errorLogs.length > 50) {
                this.errorLogs = this.errorLogs.slice(-50)
            }

            // 触发回调
            this.callbacks.onError?.(new Event('error'))

            // 安排重连
            this.scheduleReconnect()
        }
    }

    /**
     * 处理接收到的消息
     */
    private handleMessage(event: MessageEvent): void {
        const useStore = useUserStore()

        this.lastHeartbeatTime = Date.now()
        this.messagesReceived++
        // 解析 SSE 格式
        const chunk = JSON.parse(event.data)
        if (chunk?.data?.energy) {
            useStore.setMsgCost(chunk.data.energy)
        } else if (chunk?.data?.energy === 0) {
            useStore.setMsgCost(0)
        }

        if (chunk?.data?.strengthBalance) {
            useStore.setStrengthBalance(chunk.data.strengthBalance)
        } else if (chunk?.data?.strengthBalance === 0) {
            useStore.setStrengthBalance(0)
        }

        if (!chunk.data) return

        this.callbacks.onMessage?.(chunk)
    }

    /**
     * 处理连接关闭
     */
    private handleClose(errorEvent: Event | CloseEvent): void {
        // 停止心跳
        this.stopHeartbeat()

        const closeInfo = {
            readyState: this.eventSource?.readyState,
            isConnected: this.isConnected,
            isConnecting: this.isConnecting,
            reconnectAttempts: this.reconnectAttempts,
            manualClose: this.manualClose
        }

        console.log('🔌 SSE 关闭', closeInfo)

        this.isConnected = false
        this.isConnecting = false

        // 更新错误信息
        if (errorEvent instanceof CloseEvent) {
            const closeMessages: Record<number, string> = {
                1000: '正常关闭',
                1001: '端点离开',
                1006: '异常关闭',
                1011: '服务器错误'
            }
            this.connectionError = closeMessages[errorEvent.code] || `连接关闭 (${errorEvent.code})`
        } else {
            this.connectionError = '连接意外关闭'
        }

        // 记录关闭日志
        this.errorLogs.push({
            type: 'close',
            timestamp: new Date().toISOString(),
            info: { ...closeInfo, error: this.connectionError }
        })
        if (this.errorLogs.length > 50) {
            this.errorLogs = this.errorLogs.slice(-50)
        }

        // 触发回调
        this.callbacks.onClose?.(errorEvent as CloseEvent)

        // 自动重连
        if (!this.manualClose) {
            this.scheduleReconnect()
        }

        // 清理连接
        this.cleanup()
    }

    /**
     * 清理连接资源
     */
    private cleanup(): void {
        if (this.eventSource) {
            try {
                this.eventSource.close()
            } catch (e) {
                console.warn('⚠️ 关闭 EventSource 失败:', e)
            }
            this.eventSource = null
        }
    }

    /**
     * 启动心跳检测
     */
    private startHeartbeat(): void {
        if (this.heartbeatTimer) return

        this.lastHeartbeatTime = Date.now()

        this.heartbeatTimer = setInterval(() => {
            if (!this.isConnected) return

            const timeSinceHeartbeat = Date.now() - this.lastHeartbeatTime

            // 检查心跳超时
            if (timeSinceHeartbeat > this.config.heartbeatTimeout) {
                console.log('⏰ 心跳超时，尝试重连')
                this.reconnectWithNewToken()
                return
            }

            // 发送心跳（通过重新连接来保持活跃）
            // SSE 本身不需要发送心跳，心跳由服务端发送
            // 这里只做超时检测
        }, this.config.heartbeatInterval)
    }

    /**
     * 停止心跳检测
     */
    private stopHeartbeat(): void {
        if (this.heartbeatTimer) {
            clearInterval(this.heartbeatTimer)
            this.heartbeatTimer = null
        }
    }

    /**
     * 安排重连
     */
    private scheduleReconnect(): void {
        if (this.reconnectTimer) {
            console.log('🔄 重连已在计划中')
            return
        }

        if (this.manualClose) {
            return
        }

        this.reconnectAttempts++

        // 触发重连回调
        this.callbacks.onRetry?.(this.reconnectAttempts)

        // 计算重连延迟（指数退避）
        let delay = this.config.reconnectInterval

        if (this.reconnectAttempts <= 3) {
            // 前3次快速重连
            delay = 1000
        } else if (this.reconnectAttempts <= 10) {
            // 4-10次使用指数退避
            delay = Math.min(1000 * Math.pow(1.5, this.reconnectAttempts - 3), 10000)
        } else {
            // 超过10次后，使用固定间隔
            delay = 10000
        }

        // 检查网络状态
        const isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true
        if (!isOnline) {
            delay = 5000 // 离线时延长等待
        }

        console.log(`⏰ 准备重连... (${delay / 1000}秒后，第 ${this.reconnectAttempts} 次)`)

        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null

            if (!this.manualClose && !this.isConnected) {
                // 重置状态
                this.isConnecting = false
                this.isConnected = false

                // 清理旧连接
                this.cleanup()

                // 重新连接
                this.connect()
            }
        }, delay)
    }

    /**
     * 断开连接
     */
    disconnect(): void {
        console.log('🔌 手动断开 SSE')
        this.manualClose = true

        // 清理重连定时器
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer)
            this.reconnectTimer = null
        }

        // 停止心跳
        this.stopHeartbeat()

        // 清理连接
        this.cleanup()

        // 重置状态
        this.isConnected = false
        this.isConnecting = false
        this.reconnectAttempts = 0
    }

    /**
     * 销毁管理器
     */
    destroy(): void {
        this.disconnect()
        this.callbacks = {}
    }

    /**
     * 使用新 Token 重新连接
     */
    reconnectWithNewToken(): void {
        console.log('🔄 使用新 Token 重新连接...')

        // 清理重连定时器
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer)
            this.reconnectTimer = null
        }

        // 停止心跳
        this.stopHeartbeat()

        // 清理旧连接
        this.cleanup()

        // 重置状态
        this.isConnected = false
        this.isConnecting = false
        this.reconnectAttempts = 0
        this.connectionError = null
        this.manualClose = false

        // 延迟连接
        setTimeout(() => {
            if (!this.isConnected && !this.isConnecting) {
                this.connect()
            }
        }, 100)
    }

    /**
     * 获取连接状态
     */
    getStatus(): {
        isConnected: boolean
        isConnecting: boolean
        connectionError: string | null
        reconnectAttempts: number
    } {
        return {
            isConnected: this.isConnected,
            isConnecting: this.isConnecting,
            connectionError: this.connectionError,
            reconnectAttempts: this.reconnectAttempts
        }
    }

    /**
     * 检查是否真的已连接
     */
    isReallyConnected(): boolean {
        return this.isConnected &&
            this.eventSource !== null &&
            this.eventSource.readyState === EventSource.OPEN
    }

    /**
     * 获取连接统计信息
     */
    getStats(): {
        isConnected: boolean
        isConnecting: boolean
        messagesReceived: number
        reconnectAttempts: number
        lastHeartbeatTime: number
    } {
        return {
            isConnected: this.isConnected,
            isConnecting: this.isConnecting,
            messagesReceived: this.messagesReceived,
            reconnectAttempts: this.reconnectAttempts,
            lastHeartbeatTime: this.lastHeartbeatTime
        }
    }

    /**
     * 获取错误日志
     */
    getErrorLogs(): Array<{ type: string; timestamp: string; info: any }> {
        return [...this.errorLogs]
    }

    /**
     * 清除错误日志
     */
    clearErrorLogs(): void {
        this.errorLogs = []
    }
}

// 全局实例管理
let globalSSEManager: SSEManager | null = null

/**
 * 获取全局 SSE 管理器
 */
export function getSSEManager(): SSEManager {
    try {
        if (typeof window !== 'undefined') {
            const win = window as any
            if (win.$sseManager && win.$sseManager instanceof SSEManager) {
                globalSSEManager = win.$sseManager
                return win.$sseManager
            }
        }
    } catch { }

    if (!globalSSEManager) {
        // 优先使用环境变量，默认为测试环境
        const baseUrl = import.meta.env.VITE_SSE_URL
            || (import.meta.env.VITE_APP_ENV === 'production' ? 'https://api.chat.muyuhan.xin/sse/' : 'https://api.chat.test.muyuhan.xin/sse/')
        globalSSEManager = new SSEManager({ baseUrl })
        try {
            if (typeof window !== 'undefined') {
                (window as any).$sseManager = globalSSEManager
            }
        } catch { }
    }

    return globalSSEManager!
}

/**
 * 检查 SSE 是否支持
 */
export function isSSESupported(): boolean {
    return typeof EventSource !== 'undefined'
}

/**
 * 调试工具：显示 SSE 状态
 */
export function showSSEStatus(): void {
    const sseManager = getSSEManager()
    const stats = sseManager.getStats()
    const status = sseManager.getStatus()

    console.group('📊 SSE 连接状态')
    console.log('✅ 连接状态:', status.isConnected ? '已连接' : status.isConnecting ? '连接中' : '未连接')
    console.log('📶 重连次数:', status.reconnectAttempts)
    console.log('📥 接收消息数:', stats.messagesReceived)
    console.log('💓 距上次心跳:', Math.floor((Date.now() - stats.lastHeartbeatTime) / 1000), '秒')
    console.log('🌐 网络状态:', navigator.onLine ? '在线' : '离线')
    console.log('📱 SSE 支持:', isSSESupported())
    console.groupEnd()
}

// 挂载调试工具
if (typeof window !== 'undefined') {
    ; (window as any).showSSEStatus = showSSEStatus
    console.log('💡 提示：可在控制台输入 showSSEStatus() 查看 SSE 连接状态')
}

export { SSEManager }

